import { type FC, useState } from "react";

import { Column, FormField, Select } from "@hightouch/htui";

import { MonthlySchedulePicker } from "./monthly-schedule-picker";
import {
  type Frequency,
  getScheduleForFrequency,
} from "./report-schedule-frequency";
import {
  CALENDAR_RECURRENCE_SCHEDULE_TYPE,
  type CalendarRecurrenceSchedule,
  type ReportSchedule,
  ScheduleType,
  type VisualCronSchedule,
} from "./types";
import { VisualCronScheduleConfiguration } from "./visual-cron-schedule-configuration";

interface Props {
  defaultTime: string;
  isDisabled: boolean;
  schedule: VisualCronSchedule | CalendarRecurrenceSchedule;
  setSchedule: (schedule: ReportSchedule) => void;
}

export const ReportScheduleRecurrence: FC<Props> = ({
  defaultTime,
  isDisabled,
  schedule,
  setSchedule,
}) => {
  const [weeklySchedule, setWeeklySchedule] =
    useState<VisualCronSchedule | null>(
      schedule.type === ScheduleType.CUSTOM ? schedule : null,
    );
  const [monthlySchedule, setMonthlySchedule] =
    useState<CalendarRecurrenceSchedule | null>(
      schedule.type === CALENDAR_RECURRENCE_SCHEDULE_TYPE ? schedule : null,
    );
  const frequency: Frequency =
    schedule.type === CALENDAR_RECURRENCE_SCHEDULE_TYPE ? "monthly" : "weekly";
  const frequencyOptions = [
    { label: "Weekly", value: "weekly" as const },
    { label: "Monthly", value: "monthly" as const },
  ];

  const changeFrequency = (nextFrequency: Frequency) => {
    if (nextFrequency === frequency) return;

    if (nextFrequency === "monthly") {
      if (schedule.type === ScheduleType.CUSTOM) {
        setWeeklySchedule(schedule);
      }
      setSchedule(
        getScheduleForFrequency({
          cachedMonthlySchedule: monthlySchedule,
          cachedWeeklySchedule: weeklySchedule,
          currentSchedule: schedule,
          defaultTime,
          nextFrequency,
        }),
      );
      return;
    }

    if (schedule.type === CALENDAR_RECURRENCE_SCHEDULE_TYPE) {
      setMonthlySchedule(schedule);
    }
    setSchedule(
      getScheduleForFrequency({
        cachedMonthlySchedule: monthlySchedule,
        cachedWeeklySchedule: weeklySchedule,
        currentSchedule: schedule,
        defaultTime,
        nextFrequency,
      }),
    );
  };

  return (
    <Column gap={6}>
      <FormField label="Frequency">
        <Select
          isDisabled={isDisabled}
          width="xs"
          value={frequency}
          options={frequencyOptions}
          onChange={(value) => value && changeFrequency(value)}
        />
      </FormField>

      {schedule.type === CALENDAR_RECURRENCE_SCHEDULE_TYPE ? (
        <MonthlySchedulePicker
          isDisabled={isDisabled}
          schedule={schedule}
          setSchedule={setSchedule}
        />
      ) : (
        <VisualCronScheduleConfiguration
          defaultTime={defaultTime}
          isDisabled={isDisabled}
          schedule={schedule}
          setSchedule={setSchedule}
        />
      )}
    </Column>
  );
};
